import React, { useState, useEffect } from 'react';
import { useTheme, Box, Tooltip, Theme } from '@mui/material';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Legend, CartesianGrid } from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { QueryField, QueryFieldTypes } from 'system/query/QueryInfo';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import { CategoricalChartState } from 'recharts/types/chart/types';
import BarChartTooltip from './BarChartTooltip';
import { BarChartSettingsStateType, defaultSettings } from './BarChartSettings';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import { getAxisTitleFromResponse } from '../TrendChart/TrendChart';
import { MakeDrilldownQueryFromHistogramIndex } from 'components/widgets/utility/DrilldownQuery';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import { getFullFieldToColor } from 'theme/KeyToInfegyColor';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';
import AbbreviateNumber, { toAbbreviatedNumber } from 'components/formatting/AbbreviateNumber';

function BarChartContent(props: WithQueryWidgetProps): React.JSX.Element {
  const theme:Theme = useTheme();
  const { i18n, t } = useTranslation();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'bar-chart');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'bar-chart');

  const widgetSettings:BarChartSettingsStateType = props.widget?.data?.settings || defaultSettings;

  const [drilldownIsOpen, setDrilldownIsOpen] = useState<boolean>(false);
  const [drilldownQuery, setDrilldownQuery] = useState<Query | null>(null);
  const [drilldownTitle, setDrilldownTitle] = useState<string>("");
  const [hoveredBar, setHoveredBar] = useState<any>(null);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [activeSeriesRow, setActiveSeriesRow] = useState<any>(null);

  const { xLabel, yLabel } = getAxisTitleFromResponse(apiResponseInfo?.responseBody);
  const chartTitle = t("multi-axis-title", {xLabel, yLabel});

  const response = apiResponseInfo?.responseBody || []
  const keys = response?.histogram?.keys || [];
  const labels = response?.histogram?.labels || [];
  let series = response?.histogram?.series || [];

  // Remove the first series if it is the subAgg
  if (series.length > 1) {
    series = series.slice(1, 10);
  }

  // Convert the response data into a format that Recharts can use
  const chartData = labels.map((row: any, index:number) => {
    const dataRow = { date: row };
    series.reduce((memo: any, seriesRow: any) => {
      // Build a map of known key values to unknown labels (could be in any language)
      memo[seriesRow.label] = seriesRow.values[index] === null ? 0 : seriesRow.values[index].toFixed(1);
      memo.key = keys[index];
      memo.field = seriesRow.field_id;
      return memo;
    }, dataRow);
    return dataRow;
  });

  const histogramOp = originalRequest?.requestInit?.body?.aggs?.histogram?.op,
        isToDorDoW = ["time_of_day", "day_of_week"].includes(histogramOp);

  // Build each of the bars for the chart
  const chartBars = series.map((seriesRow: any, index:number) => {
    const colorKey:number = index % 10 + 1;
    const label:string = seriesRow.label;

    // Get the color from the theme
    let color = theme.colors.namedColors[`chart${colorKey}`];

    // If the color is defined in the theme, use that instead (Gender, Sentiment, etc)
    if (seriesRow.field_id && seriesRow.key && getFullFieldToColor(theme, seriesRow.field_id, seriesRow.key)) {
      color = getFullFieldToColor(theme, seriesRow.field_id, seriesRow.key);
    }

    // Stack the series segments to create a multi-series bar
    return (
      <Bar key={index} dataKey={seriesRow.label} stackId="a" fill={color}
        onClick={() => {
          setActiveSeriesRow(seriesRow)
          // We cannot drill into Time of Day or Day of Week histograms, there are no supported agg fields
          if (!isToDorDoW) {
            setDrilldownIsOpen(true);
          }
        }}
        onMouseEnter={() => {
          setHoveredBar(seriesRow);
          setHoveredColor(color);
        }}
        onMouseLeave={() => {
          setHoveredBar(null);
          setHoveredColor(null);
        }}
      />
    );
  });

  // Get the field types from the query info
  const fieldTypes = props?.queryInfo?.fields?.filter((field: QueryField) => {
    if (field.type === QueryFieldTypes.integer || field.type === QueryFieldTypes.keyword || field.type === QueryFieldTypes.keywords || field.type === QueryFieldTypes.timestamp) {
      return { id: field.id, type: field.type };
    }
  }) || [];

  // Get the field type
  const getFieldType = (fieldId: string, fieldTypes: any[]) => {
    return fieldTypes?.find((field: any) => field.id === fieldId)?.type;
  };

  // Build the Y-axis query
  const buildYAxisQuery = (query: any, yAxisField: string, yAxisValue: string, yAxisLabel: string) => {
    query.values.push({
      op: "contains",
      fields: [yAxisField],
      value: yAxisValue,
    });
    return `${yAxisLabel}: `;
  };

  // Build the X-axis query
  const buildXAxisQuery = (query: any, xAxisField: string, seriesStartValue: any, seriesEndValue: any, isLastIndex: boolean, isNumericField: boolean) => {
    if (isNumericField) {
      if (isLastIndex) {
        query.values.push({
          op: ">=",
          field: xAxisField,
          value: seriesStartValue,
        });
      } else {
        query.values.push({
          op: "range",
          field: xAxisField,
          lower: seriesStartValue,
          upper: seriesEndValue,
        });
      }
    } else {
      query.values.push({
        op: "contains",
        field: xAxisField,
        value: seriesStartValue,
      });
    }
  };

  // Build the query label
  const buildQueryLabel = (isNumericField: boolean, isLastIndex: boolean, seriesStartLabel: string, seriesEndLabel: string, queryLabel: string) => {
    if (isNumericField) {
      return isLastIndex
        ? `${queryLabel} ${i18n.t("after")} ${seriesEndLabel}`
        : `${queryLabel} ${seriesStartLabel} ${i18n.t("query-form.to-lowercase")} ${seriesEndLabel}`;
    } else {
      return `${queryLabel} ${seriesStartLabel}`;
    }
  };

  // Build the drilldown query
  useEffect(() => {
    if (!activeSeriesRow || !props.queryInfo?.fields) {
      return;
    }

    const xAxisField = response.histogram.series[0].field_id;
    const xAxisFieldType = props.queryInfo?.fields.find((field: QueryField) => field.id === xAxisField)?.type;

    if (xAxisFieldType === "timestamp") { // If the x-axis is a timestamp, we need to use the histogram data
      const drilldownQuery = MakeDrilldownQueryFromHistogramIndex(response.histogram, xAxisField, activeIndex);
      drilldownQuery?.query && setDrilldownQuery(drilldownQuery?.query);
      drilldownQuery?.title && setDrilldownTitle(drilldownQuery?.title);
    } else { // Otherwise, we need to build the query ourselves
      const yAxisField = activeSeriesRow.field_id;
      const yAxisValue = activeSeriesRow.key;
      const yAxisLabel = activeSeriesRow.label === "subAgg" ? activeSeriesRow.field_label : activeSeriesRow.label;

      const xAxisFieldType = getFieldType(xAxisField, fieldTypes);
      const yAxisFieldType = getFieldType(yAxisField, fieldTypes);

      const seriesStartValue = keys[activeIndex];
      const seriesStartLabel = labels[activeIndex];
      const seriesEndValue = keys[activeIndex + 1] || keys[keys.length - 1];
      const seriesEndLabel = labels[activeIndex + 1] || labels[labels.length - 1];

      const isKeywordField = yAxisFieldType === "keyword" || yAxisFieldType === "keywords";
      const isNumericField = xAxisFieldType === "timestamp" || xAxisFieldType === "integer";
      const isLastIndex = activeIndex === keys.length - 1;

      let query: any = { op: "and", values: [] };
      let queryLabel = ``;

      // Build the Y-axis query if applicable
      if (series.length > 1 && yAxisValue !== "_count" && isKeywordField) {
        queryLabel = buildYAxisQuery(query, yAxisField, yAxisValue, yAxisLabel);
      }

      // Build the X-axis query
      buildXAxisQuery(query, xAxisField, seriesStartValue, seriesEndValue, isLastIndex, isNumericField);

      // Build the query label
      queryLabel = buildQueryLabel(isNumericField, isLastIndex, seriesStartLabel, seriesEndLabel, queryLabel);

      // Set the drilldown query and title
      setDrilldownQuery(new Query({ query }));
      setDrilldownTitle(queryLabel);
    }
  }, [drilldownIsOpen]);

  return (
    <>
      {(chartData.length !== 0) ? (
        <WidgetLayout header={
          <WidgetTitle
            defaultTitle={chartTitle}
            settingsTitle={widgetSettings?.widgetTitle}
            customTitle={widgetSettings?.customWidgetTitle}
            alignment={widgetSettings?.widgetTitleAlignment}
            widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
        } >
          <Tooltip
            title={(hoveredBar) ? <BarChartTooltip bar={hoveredBar} index={activeIndex} color={hoveredColor} labels={labels} xAxisLabel={response.histogram.series[0].field_label}/> : ''}
            open={Boolean(hoveredBar)}
            followCursor={true}
          >
            <Box sx={{
              height: "100%"
            }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartData}
                  style={{ cursor: "pointer" }}
                  onClick={(nextState: CategoricalChartState) => { setActiveIndex(nextState.activeTooltipIndex || 0) }}
                  onMouseMove={(nextState: CategoricalChartState) => { setActiveIndex(nextState.activeTooltipIndex || 0) }}
                >
                  {/* Chart Elements via Widget Settings */}
                  {widgetSettings.xAxis && <XAxis dataKey="date" interval="preserveStartEnd" />}
                  {widgetSettings.yAxis && (
                    <YAxis
                      domain={['dataMin', 'dataMax']}
                      tickCount={10}
                      tickFormatter={(tick) => {
                        const options = tick >= 10 ? { maximumFractionDigits: 0 } : { maximumFractionDigits: 2 };
                        return toAbbreviatedNumber(i18n, Number(tick), options);
                      }}
                      />
                  )}
                  {widgetSettings.showGrid && <CartesianGrid strokeDasharray="1 5" />}
                  {widgetSettings.showLegend && <Legend />}
                  {/* We need this Recharts tooltip to show the hover background effects */}
                  <RechartsTooltip content={() => null} />
                  {/* Display the Bars */}
                  {chartBars}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Tooltip>
        </WidgetLayout>
      ) : (
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      )}

      {/* Drilldown */}
      {drilldownIsOpen && drilldownQuery &&
        <DashboardQueryDrilldownDialog
          activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
          widgetRequestInfo={originalRequest}
          queryInfo={props.queryInfo}
          title={drilldownTitle}
          drilldownQuery={drilldownQuery}
          onClose={() => setDrilldownIsOpen(false)} />
      }
    </>
  );
}

export default withQueryWidget(BarChartContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});